import type { ExpressionSpecification, SymbolLayerSpecification } from 'maplibre-gl';
import { rgbCss } from '@/core/utils';
import type { StylePart } from '@/features/map';
import { WIND_COLOR_STOPS } from '../model/windColors';
import { MARKER_RADIUS, MARKER_STROKE } from './markerImage';
import { GUST_TEXT_SIZE, WIND_TEXT_SIZE, type WindLabel } from './toFeatures';

export const ARROW_IMAGE = 'station-arrow';

export const STATIONS_SOURCE = 'stations';
export const STATION_CIRCLES_LAYER = 'station-circles';
export const STATION_LAYERS = ['station-arrows', STATION_CIRCLES_LAYER, 'station-wind', 'station-gust'];

const ATTRIBUTION = 'Weather data © <a href="https://synopticdata.com">Synoptic Data</a>';

/** Feature properties, see toFeatures(). */
export interface StationProperties extends WindLabel {
  id: string;
  /** Wind speed, m/s. */
  speed: number;
  /** Rotation of the arrow (downwind), degrees, or -1 for no arrow (calm or no direction). */
  rotation: number;
  /** Drawing order: better stations on top. */
  rank: number;
}

/**
 * Feature state, set by StationLayer: `fade` (0–1) animates stations in and out; `hover` and `selected`
 * bring a station to full opacity.
 */
export interface StationState {
  fade?: number;
  hover?: boolean;
  selected?: boolean;
}

/** Markers are slightly see-through, so the map stays readable under them. */
const RESTING_OPACITY = 0.8;

const fade: ExpressionSpecification = ['coalesce', ['feature-state', 'fade'], 0];
const emphasis: ExpressionSpecification = [
  'case',
  ['any', ['boolean', ['feature-state', 'hover'], false], ['boolean', ['feature-state', 'selected'], false]],
  1,
  RESTING_OPACITY,
];

const opacity: ExpressionSpecification = ['*', fade, emphasis];

const windColor: ExpressionSpecification = [
  'interpolate',
  ['linear'],
  ['get', 'speed'],
  ...WIND_COLOR_STOPS.flatMap(([v, c]) => [v, rgbCss(c)]),
];

const TEXT = '#1d1d1f';
const OUTLINE = '#fff';
const FONT = ['Open Sans Bold'];

/** Shared by all station symbols: always drawn, in rank order, never hidden by collisions. */
const SYMBOL_LAYOUT: SymbolLayerSpecification['layout'] = {
  'symbol-sort-key': ['get', 'rank'],
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
  'text-allow-overlap': true,
  'text-ignore-placement': true,
};

export const stationsStyle = (visible: boolean): StylePart => {
  const visibility = visible ? 'visible' : 'none';
  return {
    sources: {
      [STATIONS_SOURCE]: {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
        promoteId: 'id',
        attribution: ATTRIBUTION,
      },
    },
    layers: [
      {
        id: 'station-arrows',
        type: 'symbol',
        source: STATIONS_SOURCE,
        filter: ['>=', ['get', 'rotation'], 0],
        layout: {
          ...SYMBOL_LAYOUT,
          visibility,
          'icon-image': ARROW_IMAGE,
          'icon-rotate': ['get', 'rotation'],
          'icon-rotation-alignment': 'map',
          'icon-pitch-alignment': 'viewport',
        },
        paint: {
          // Filled like its circle, outlined like it: a white arrow vanishes on light basemaps.
          'icon-color': windColor,
          'icon-halo-color': OUTLINE,
          'icon-halo-width': MARKER_STROKE,
          'icon-opacity': opacity,
        },
      },
      {
        id: STATION_CIRCLES_LAYER,
        type: 'circle',
        source: STATIONS_SOURCE,
        layout: { visibility, 'circle-sort-key': ['get', 'rank'] },
        paint: {
          'circle-radius': MARKER_RADIUS,
          'circle-color': windColor,
          'circle-opacity': opacity,
          'circle-stroke-color': OUTLINE,
          'circle-stroke-width': MARKER_STROKE,
          'circle-stroke-opacity': opacity,
          'circle-pitch-scale': 'viewport',
        },
      },
      {
        id: 'station-wind',
        type: 'symbol',
        source: STATIONS_SOURCE,
        layout: {
          ...SYMBOL_LAYOUT,
          visibility,
          'text-field': ['get', 'wind'],
          'text-font': FONT,
          'text-size': ['*', WIND_TEXT_SIZE, ['get', 'scale']],
          'text-offset': ['get', 'windOffset'],
        },
        paint: { 'text-color': TEXT, 'text-opacity': fade },
      },
      {
        id: 'station-gust',
        type: 'symbol',
        source: STATIONS_SOURCE,
        filter: ['!=', ['get', 'gust'], ''],
        layout: {
          ...SYMBOL_LAYOUT,
          visibility,
          'text-field': ['get', 'gust'],
          'text-font': FONT,
          'text-size': ['*', GUST_TEXT_SIZE, ['get', 'scale']],
          'text-offset': ['get', 'gustOffset'],
        },
        paint: { 'text-color': TEXT, 'text-opacity': fade },
      },
    ],
  };
};
